import { AppStorage } from "@bbwt/utils/app-storage";
import { IEpisode } from "@project/models/episodes/episode";
import { Prescription } from "@project/models/prescriptions/prescription";
import { PrescriptionLine } from "@project/models/prescriptions/prescription-line";

export class PrescriptionOfflineStorage {
    private static readonly cacheKey = "api/prescriptions";

    static save(prescription: Prescription): Prescription {
        return AppStorage.setOfflineEntity<Prescription>(this.cacheKey, prescription);
    }

    static get(id: string): Prescription {
        return AppStorage.getOfflineEntity<Prescription>(id, this.cacheKey);
    }

    static getAll(): Prescription[] {
        return AppStorage.getOfflineEntities<Prescription>(this.cacheKey);
    }

    static update(prescription: Prescription): Prescription {
        return AppStorage.replaceOfflineEntity<Prescription>(prescription, this.cacheKey);
    }

    static remove(id: string): Prescription {
        return AppStorage.extractOfflineEntity<Prescription>(id, this.cacheKey);
    }

    static updateEpisode(episodeId: string, episode: IEpisode): void {
        const prescriptions: Prescription[] = this.getAll().filter(p => p.episodeId == episodeId);
        prescriptions.forEach(p => this.update({ ...p, episodeId: episode.id, episode }));
    }

    static addPrescriptionLine(prescriptionId: string, line: PrescriptionLine): Prescription {
        let prescription: Prescription = this.get(prescriptionId);
        if (prescription != null) {
            prescription.prescriptionLines.push(line);
            prescription = this.update(prescription);
        }
        return prescription;
    }

    static getPatientPrescription(patientId: string): Prescription {
        return this.getAll().find(prescription => prescription.patientId === patientId);
    }

    static updatePatientId(oldPatientId: string, patientId: string): void {
        const patientPrescriptions = this.getAll()
            .filter(p => p.patientId == oldPatientId)
            .map(p => ({ ...p, patientId }));

        patientPrescriptions.forEach(p => {
            p.prescriptionLines.forEach(pl => (pl.patientId = patientId));
            this.update(p);
        });
    }

    static updatePrescriptionLine(prescriptionId: string, prescriptionLine: PrescriptionLine): Prescription {
        let prescription = this.get(prescriptionId);
        if (prescription != null) {
            prescription.prescriptionLines = prescription.prescriptionLines?.map(line =>
                line.id === prescriptionLine.id ? prescriptionLine : line
            );
            prescription = this.update(prescription);
        }
        return prescription;
    }

    static removePrescriptionLine(prescriptionId: string, prescriptionLineId: string): Prescription {
        let prescription = this.get(prescriptionId);
        if (prescription != null) {
            prescription.prescriptionLines = prescription.prescriptionLines?.filter(line => line.id !== prescriptionLineId);
            prescription = this.update(prescription);
        }
        return prescription;
    }
}
